import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { patientAllergyInputSchema, type PatientAllergyInput } from "@dosa/validation";
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  FieldError,
  FieldLabel,
  Input,
  MrpSeverityBadge,
  PageLoading,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@dosa/ui";
import { useAuth } from "../../hooks/useAuth";
import { addPatientAllergy, listPatientAllergies } from "../../lib/api-client";

const ALLERGY_SEVERITIES = ["MILD", "MODERATE", "SEVERE", "LIFE_THREATENING", "UNKNOWN"] as const;

export function PatientAllergiesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const patientId = user!.patientId!;
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: allergies, isLoading } = useQuery({
    queryKey: ["patient-allergies", patientId],
    queryFn: () => listPatientAllergies(patientId),
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PatientAllergyInput>({
    resolver: zodResolver(patientAllergyInputSchema),
    defaultValues: { severity: "UNKNOWN", allergenType: "OTHER" },
  });

  const mutation = useMutation({
    mutationFn: (input: PatientAllergyInput) => addPatientAllergy(patientId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-allergies", patientId] });
      reset();
      setOpen(false);
    },
  });

  if (isLoading) return <PageLoading />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-charcoal-900">{t("patient.allergies.title")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger render={<Button />}>
            <Plus className="h-4 w-4" /> {t("patient.allergies.addAllergy")}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("patient.allergies.addAllergy")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4" noValidate>
              <div>
                <FieldLabel htmlFor="freeTextAllergen">{t("patient.allergies.title")}</FieldLabel>
                <Input id="freeTextAllergen" placeholder="e.g. Penicillin" {...register("freeTextAllergen")} />
                <FieldError>{errors.freeTextAllergen?.message}</FieldError>
              </div>
              <div>
                <FieldLabel htmlFor="reaction">{t("patient.allergies.reaction")}</FieldLabel>
                <Input id="reaction" placeholder="e.g. Rash" {...register("reaction")} />
              </div>
              <div>
                <FieldLabel htmlFor="severity">{t("patient.allergies.severity")}</FieldLabel>
                <Controller
                  control={control}
                  name="severity"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      items={Object.fromEntries(ALLERGY_SEVERITIES.map((s) => [s, s.replaceAll("_", " ")]))}
                    >
                      <SelectTrigger id="severity">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ALLERGY_SEVERITIES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s.replaceAll("_", " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  {t("common.cancel")}
                </Button>
                <Button type="submit" isLoading={isSubmitting}>
                  {t("common.save")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {!allergies || allergies.length === 0 ? (
            <EmptyState className="border-0" title={t("patient.allergies.empty")} />
          ) : (
            <ul className="divide-y divide-charcoal-100">
              {allergies.map((a) => (
                <li key={a.id} className="flex items-center justify-between px-5 py-4">
                  <div>
                    <p className="font-medium text-charcoal-800">{a.freeTextAllergen}</p>
                    {a.reaction && <p className="text-sm text-charcoal-400">{a.reaction}</p>}
                  </div>
                  <MrpSeverityBadge severity={a.severity === "LIFE_THREATENING" ? "CRITICAL" : a.severity === "SEVERE" ? "HIGH" : a.severity === "MODERATE" ? "MODERATE" : "LOW"} label={a.severity.replaceAll("_", " ")} />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
